import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Spin } from "antd";
import CgIcon from "./CgIcon";
import { debounce } from "../../../helpers/utility";
import { globalSearchApi } from "../../../services/medication.services";
import styles from "./PharmacyGlobalSearch.module.css";

/*
 * The endpoint's own rule, not a guess: one character comes back as
 * 400 {"q": "Enter at least 2 characters."}. Requests below it are not sent,
 * and the panel says so rather than staying shut and looking broken.
 */
const MIN_QUERY = 2;

/*
 * What the endpoint returns, and where each kind of result goes.
 *
 * The pharmacy records open their own screen. A patient, an admission or a
 * consultation opens a new Sale Entry with that person already chosen - which
 * is what a pharmacist wants from finding them, and keeps the result inside
 * this module rather than sending them to a Receptionist screen their role may
 * not reach.
 */
const saleEntryFor = (prefill) => ({
  to: "/pharmacy/orders/sale-entry/new",
  state: { prefill },
});

const GROUPS = [
  {
    docType: "patient",
    label: "Patients",
    open: (row) =>
      saleEntryFor({
        sale_type: "PATIENT",
        patient: row.id,
        patient_name: row.title,
        patient_mrn: row.reference_id,
      }),
  },
  {
    docType: "ip",
    label: "IP Admissions",
    open: (row) =>
      saleEntryFor({
        sale_type: "IP",
        ip_admission: row.id,
        admission_label: [row.title, row.reference_id].filter(Boolean).join(" - "),
      }),
  },
  {
    docType: "op",
    label: "OP Consultations",
    open: (row) =>
      saleEntryFor({
        sale_type: "OP",
        op_consultation: row.id,
        admission_label: [row.title, row.reference_id].filter(Boolean).join(" - "),
      }),
  },
  {
    docType: "pharmacy_order",
    label: "Orders",
    open: (row) => ({ to: `/pharmacy/orders/sale-entry/${row.id}` }),
  },
  {
    docType: "pharmacy_bill",
    label: "Bills",
    open: (row) => ({ to: `/pharmacy/billing/${row.id}` }),
  },
  {
    docType: "purchase_entry",
    label: "Purchase Entries",
    open: (row) => ({ to: `/pharmacy/purchase-entry/${row.id}` }),
  },
  {
    docType: "purchase_order",
    label: "Purchase Orders",
    open: (row) => ({ to: `/pharmacy/purchase-order/${row.id}` }),
  },
];

const PharmacyGlobalSearch = () => {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const abortRef = useRef(null);
  // Guards against a slow earlier response landing after a later one.
  const requestIdRef = useRef(0);

  const [term, setTerm] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const runSearch = useCallback((text) => {
    const query = String(text || "").trim();
    if (query.length < MIN_QUERY) {
      setResults(null);
      setLoading(false);
      return;
    }

    // Drop the request the previous keystroke started - its answer is stale.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setLoading(true);
    globalSearchApi(query, { signal: controller.signal })
      .then((res) => {
        if (requestIdRef.current !== requestId) return;
        setResults(res?.data?.results || {});
      })
      .catch(() => {
        // An aborted request is not a failure, and a later one is already running.
        if (requestIdRef.current === requestId) setResults({});
      })
      .finally(() => {
        if (requestIdRef.current === requestId) setLoading(false);
      });
  }, []);

  /*
   * Debounced so a typed word is one request rather than one per letter. Held
   * in a ref-stable memo so the timer is not rebuilt on every render, which
   * would defeat the debounce entirely.
   */
  const debouncedSearch = useMemo(() => debounce(runSearch, 350), [runSearch]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Clicking anywhere else closes the panel.
  useEffect(() => {
    if (!open) return undefined;
    const onDocumentClick = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [open]);

  const groups = useMemo(() => {
    if (!results) return [];
    return GROUPS.map((group) => ({
      ...group,
      rows: results[group.docType] || [],
    })).filter((group) => group.rows.length);
  }, [results]);

  const handleChange = (event) => {
    const next = event.target.value;
    setTerm(next);
    setOpen(true);
    if (next.trim().length < MIN_QUERY) {
      setResults(null);
      setLoading(false);
      return;
    }
    debouncedSearch(next);
  };

  const go = (group, row) => {
    setOpen(false);
    setTerm("");
    setResults(null);
    const { to, state } = group.open(row);
    navigate(to, state ? { state } : undefined);
  };

  const typed = term.trim().length;
  const showPanel = open && typed > 0;
  const tooShort = typed > 0 && typed < MIN_QUERY;

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <Input
        className={styles.field}
        allowClear
        value={term}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        prefix={<CgIcon name="search" size={15} stroke={2} style={{ color: "#98a2b3" }} />}
        placeholder="Search patient, order, bill or purchase"
        aria-label="Search pharmacy records"
      />

      {showPanel && (
        <div className={styles.panel} role="listbox">
          {tooShort ? (
            <div className={styles.state}>
              Type at least {MIN_QUERY} characters to search
            </div>
          ) : loading && !groups.length ? (
            <div className={styles.state}>
              <Spin size="small" /> <span style={{ marginLeft: 8 }}>Searching…</span>
            </div>
          ) : groups.length ? (
            groups.map((group) => (
              <div key={group.docType}>
                <div className={styles.groupLabel}>{group.label}</div>
                {group.rows.map((row) => (
                  <button
                    key={`${group.docType}-${row.id}`}
                    type="button"
                    className={styles.row}
                    onClick={() => go(group, row)}
                  >
                    <span className={styles.rowMain}>
                      <span className={styles.rowTitle}>{row.title || "-"}</span>
                      {row.subtitle ? (
                        <span className={styles.rowSub}>{row.subtitle}</span>
                      ) : null}
                    </span>
                    {row.reference_id ? (
                      <span className={styles.rowRef}>{row.reference_id}</span>
                    ) : null}
                  </button>
                ))}
              </div>
            ))
          ) : (
            <div className={styles.state}>
              Nothing matches “{term.trim()}”
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PharmacyGlobalSearch;
